import { useState, useEffect, useRef } from "react";
import { collection, query, orderBy, getDocs, limit, startAfter, where, onSnapshot, addDoc, doc, updateDoc, serverTimestamp, getDoc, DocumentSnapshot } from "firebase/firestore";
import { db, auth, sanitizeFirestoreId } from "@/lib/firebase";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageHistory } from "@/components/MessageHistory";
import { AudioPlayer } from "@/components/AudioPlayer";
import { format } from "date-fns";
import { Clock, Eye, EyeOff, Image, FileText, Video, File, Mic, ChevronDown, ChevronUp, Shield, ShieldAlert, ImageOff, ThumbsUp, ThumbsDown, Info, MessageSquare, MessageSquareDashed, Sticker } from "lucide-react";
import { ConversationContext } from "@/components/ConversationContext";
import { type Conversation } from "@/components/ConversationList";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Toggle } from "@/components/ui/toggle";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMediaQuery, useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";
import { json } from "stream/consumers";
// ADC-006A F12: getScoreColorClass extracted from component to shared util
import { getScoreColorClass } from "@/lib/incidentUtils";

export interface VoiceData {
    mimetype: string | null;
    duration: number | null;
    fileSize: string | null;
    transcription: string | null;
}

interface ConversationMessageData {
  id: string;
  messageTimestamp: number;
  type: 'text' | 'image' | 'extendedText' | 'stickerMessage' | 'audioMessage' | 'other';
  fromMe: boolean;
  pushName?: string;
  remoteJid?: string;
  message?: {
    conversation?: string;
    imageMessage?: {
      caption?: string;
      url?: string;
      fileSha256?: string;
    };
    extendedTextMessage?: {
      text: string;
    };
    audioMessage?: {
      url: string;
        mimetype: string;
        fileLength: string;
        seconds: number;
        mediaKey: string;
        mediaKeyTimestamp: string;
    };
    editedMessage?: {
      message?: {
          protocolMessage?: {
              key: {
                  id: string;
                  fromMe: boolean;
                  remoteJid: string;
              };
              editedMessage?: {
                  conversation?: string;
                  imageMessage?: {
                      caption?: string;
                  };
              };
          };
      };
    };
  };
  edits?: ConversationMessageData[] | undefined;
  sender?: {
    id?: string;
    name?: string;
    phone?: string;
    contact?: {
      name?: string;
      notify?: string;
    };
  };
  isFlaggedMessage: boolean;
  isDeleted?: boolean;
  moderation?: {
    text?: {
      categories?: Record<string, boolean>;
      categoryScores?: Record<string, number>;
      reason?: string;
    };
    image?: {
      categories?: Record<string, boolean>;
      categoryScores?: Record<string, number>;
      reason?: string;
    };
  };
  content?: {
    storagePath?: string;
    mimeType?: string;
    voiceData?: VoiceData;

  };
  evaluation?: {
    decision?: {
      userFriendlyExplanation?: string;
    };
  };
}

interface ConversationViewProps {
  connectionId: string;
  conversation: Conversation;
  showFlaggedOnly?: boolean;
  onToggleFlaggedOnly?: () => void;
  className?: string;
}

/**
 * Map a moderation subcollection document to the ConversationMessageData
 * interface used by the rendering logic.
 *
 * moderation docs have: rawMessage (WhatsApp message obj), fromMe, pushName,
 * messageTimestamp, isFlagged, text/image/voice/sticker (moderation results),
 * messageType (incident format).
 */
function mapModerationDocToMessage(docId: string, data: Record<string, unknown>): ConversationMessageData {
  const rawMessage = (data.rawMessage as Record<string, unknown>) ?? {};

  // Detect frontend message type from rawMessage content
  let msgType: ConversationMessageData['type'] = 'other';
  if (rawMessage.stickerMessage) msgType = 'stickerMessage';
  else if (rawMessage.audioMessage) msgType = 'audioMessage';
  else if (rawMessage.imageMessage) msgType = 'image';
  else if (rawMessage.extendedTextMessage) msgType = 'extendedText';
  else if (rawMessage.conversation) msgType = 'text';

  // Build moderation sub-object for the UI
  const moderationObj: ConversationMessageData['moderation'] = {};
  const textMod = data.text as Record<string, unknown> | null;
  const imageMod = data.image as Record<string, unknown> | null;
  if (textMod) {
    moderationObj.text = {
      categories: (textMod.categories as Record<string, boolean>) ?? undefined,
      categoryScores: (textMod.categoryScores as Record<string, number>) ?? undefined,
      reason: (textMod.reason as string) ?? undefined,
    };
  }
  if (imageMod) {
    moderationObj.image = {
      categories: (imageMod.categories as Record<string, boolean>) ?? undefined,
      categoryScores: (imageMod.categoryScores as Record<string, number>) ?? undefined,
      reason: (imageMod.reason as string) ?? undefined,
    };
  }

  // Build content object (voice transcript, image storage, text)
  const contentObj: ConversationMessageData['content'] = {};
  const voiceMod = data.voice as Record<string, unknown> | null;
  if (voiceMod?.moderated && typeof voiceMod.moderated === 'string') {
    const audioMsg = (rawMessage.audioMessage as Record<string, unknown>) ?? {};
    contentObj.voiceData = {
      transcription: voiceMod.moderated as string,
      duration: (audioMsg.seconds as number) ?? null,
      mimetype: (audioMsg.mimetype as string) ?? null,
      fileSize: null,
    };
  }
  const imageMsg = rawMessage.imageMessage as Record<string, unknown> | null;
  if (imageMsg?.caption) {
    // caption is embedded in rawMessage
  }

  // Build evaluation stub for flagged messages
  let evaluation: ConversationMessageData['evaluation'] = undefined;
  if (data.isFlagged) {
    // Gather flagged category names for the explanation
    const flaggedCats: string[] = [];
    for (const modKey of ['text', 'image', 'voice', 'sticker'] as const) {
      const mod = data[modKey] as Record<string, unknown> | null;
      if (mod?.flagged && mod?.categories) {
        const cats = mod.categories as Record<string, boolean>;
        for (const [k, v] of Object.entries(cats)) {
          if (v && !flaggedCats.includes(k)) flaggedCats.push(k);
        }
      }
    }
    evaluation = {
      decision: {
        userFriendlyExplanation: flaggedCats.length > 0
          ? `Message flagged for: ${flaggedCats.join(', ')}`
          : 'Message flagged by content filter',
      },
    };
  }

  return {
    id: docId,
    messageTimestamp: (data.messageTimestamp as number) ?? 0,
    type: msgType,
    fromMe: (data.fromMe as boolean) ?? false,
    pushName: (data.pushName as string) ?? undefined,
    message: rawMessage as ConversationMessageData['message'],
    isFlaggedMessage: (data.isFlagged as boolean) ?? false,
    moderation: Object.keys(moderationObj).length > 0 ? moderationObj : undefined,
    content: Object.keys(contentObj).length > 0 ? contentObj : undefined,
    evaluation,
  };
}

export function ConversationView({
  connectionId,
  conversation,
  showFlaggedOnly = false,
  onToggleFlaggedOnly,
  className = ""
}: ConversationViewProps) {
  const [messages, setMessages] = useState<ConversationMessageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenMessages, setHiddenMessages] = useState<Record<string, boolean>>({});
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [loadingAudios, setLoadingAudios] = useState<Record<string, boolean>>({});
  const [expandedHistories, setExpandedHistories] = useState<Record<string, boolean>>({});
  const [expandedExplanations, setExpandedExplanations] = useState<Record<string, boolean>>({});
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState<Record<string, string | null>>({});
  const [submittingFeedback, setSubmittingFeedback] = useState<Record<string, boolean>>({});
  const initialLastViewTimestampRef = useRef<number | null>(null);
  const isFirstRenderRef = useRef(true);
  const hasCompletedLoadingRef = useRef(false);
  const isMobile = useIsMobile();
  
  const lastDocumentRef = useRef<DocumentSnapshot | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const storage = getStorage();
  const messagesListenerRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!connectionId || !conversation) return;
    
    setLoading(true);
    setError(null);
    hasCompletedLoadingRef.current = false;
    
    if (messagesListenerRef.current) {
      messagesListenerRef.current();
      messagesListenerRef.current = null;
    }
    
    lastDocumentRef.current = null;
    
    const getConversationTimestamp = async () => {
      try {
        const conversationRef = doc(db, `connections/${connectionId}/conversations/${conversation.id}`);
        const conversationDoc = await getDoc(conversationRef);
        
        if (conversationDoc.exists() && conversationDoc.data().lastFlaggedReviewAt) {
          initialLastViewTimestampRef.current = Math.floor(conversationDoc.data().lastFlaggedReviewAt.toDate().getTime() / 1000);
          console.log("Initial last view timestamp: ", initialLastViewTimestampRef.current);
        } else {
          initialLastViewTimestampRef.current = null;
        }
      } catch (err) {
        console.error("Error getting conversation timestamp:", err);
        initialLastViewTimestampRef.current = null;
      }
    };
    
    getConversationTimestamp().then(() => {
      if (conversation.flaggedCount > 0) {
        updateLastFlaggedReviewTimestamp();
      }
    });
    
    // Read from moderation subcollection (has ALL messages: flagged + non-flagged)
    const messagesRef = collection(db, `connections/${connectionId}/conversations/${conversation.id}/moderation`);
    const messagesQuery = query(
      messagesRef,
      orderBy("messageTimestamp", "desc"),
      limit(30)
    );

    try {
      const unsubscribe = onSnapshot(
        messagesQuery,
        (snapshot) => {
          try {
            if (snapshot.empty) {
              setMessages([]);
              setHasMoreMessages(false);
              setLoading(false);
              hasCompletedLoadingRef.current = true;
              return;
            }

            lastDocumentRef.current = snapshot.docs[snapshot.docs.length - 1];

            const messagesData = snapshot.docs.map(doc =>
              mapModerationDocToMessage(doc.id, doc.data() as Record<string, unknown>)
            );
            
            setMessages(messagesData);
            setHasMoreMessages(messagesData.length === 30);
            setLoading(false);
            hasCompletedLoadingRef.current = true;
          } catch (err) {
            console.error("Error in messages listener:", err);
            setError(`Failed to listen for messages: ${err instanceof Error ? err.message : String(err)}`);
            setLoading(false);
            hasCompletedLoadingRef.current = true;
          }
        },
        (err) => {
          console.error("Error in messages listener:", err);
          setError(`Failed to listen for messages: ${err.message}`);
          setLoading(false);
          hasCompletedLoadingRef.current = true;
        }
      );
      
      messagesListenerRef.current = unsubscribe;
    } catch (error) {
      console.error("Error setting up messages listener:", error);
      setError("Failed to set up messages listener");
      setLoading(false);
      hasCompletedLoadingRef.current = true;
    }
    
    return () => {
      if (messagesListenerRef.current) {
        messagesListenerRef.current();
        messagesListenerRef.current = null;
      }
    };
  }, [connectionId, conversation]);

  const updateLastFlaggedReviewTimestamp = async () => {
    if (!connectionId || !conversation?.id || !auth.currentUser) {
      console.log("Missing required data for timestamp update:", {
        connectionId: !!connectionId,
        conversationId: !!conversation?.id,
        userAuthenticated: !!auth.currentUser
      });
      return;
    }
    
    try {
      const conversationPath = `connections/${connectionId}/conversations/${conversation.id}`;
      console.log(`Attempting to update timestamp for conversation at path: ${conversationPath}`);
      
      const conversationRef = doc(db, conversationPath);
      
      const conversationDoc = await getDoc(conversationRef);
      
      if (!conversationDoc.exists()) {
        console.log(`Conversation document does not exist: ${conversationPath}`);
        return;
      }
      
      await updateDoc(conversationRef, {
        lastFlaggedReviewAt: serverTimestamp(),
        lastViewedBy: auth.currentUser.uid
      });
      
      console.log(`Successfully updated timestamp for conversation ${conversation.id}`);
    } catch (err: unknown) {
      const error = err as { code?: string };
      if (error.code === 'permission-denied') {
        console.error(`Permission denied when updating document. Check Firestore rules.`);
      } else {
        console.error(`Error updating lastFlaggedReviewAt for conversation ${conversation.id}:`, err);
        toast({
          title: "Error",
          description: "Failed to update conversation timestamp",
          variant: "destructive"
        });
      }
    }
  };

  useEffect(() => {
    loadImageUrls();
    loadAudioUrls();
  }, [messages]);

  useEffect(() => {
    const initialHiddenState: Record<string, boolean> = {};
    messages.filter(m => m.isFlaggedMessage).forEach(message => {
      if (hiddenMessages[message.id] === undefined) {
        initialHiddenState[message.id] = true;
      }
    });
    
    if (Object.keys(initialHiddenState).length > 0) {
      setHiddenMessages(prev => ({
        ...prev,
        ...initialHiddenState
      }));
    }
  }, [messages, hiddenMessages]);

  // Component to render text with clickable links
  const LinkifiedText = ({ text }: { text: string }) => {
    if (!text) return null;
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Añadir el enlace como componente
      const url = match[0];
      parts.push(
        <a 
          key={match.index} 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-500 hover:underline break-all"
        >
          {url}
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return (
      <div className="whitespace-pre-wrap text-foreground break-words overflow-hidden">
        {parts}
      </div>
    );
  };

  const loadMoreMessages = () => {
    if (!lastDocumentRef.current || messages.length === 0 || loadingMore) return;
    
    setLoadingMore(true);
    
    const messagesRef = collection(db, `connections/${connectionId}/conversations/${conversation.id}/moderation`);

    const moreMessagesQuery = query(
      messagesRef,
      orderBy("messageTimestamp", "desc"),
      startAfter(lastDocumentRef.current),
      limit(showFlaggedOnly ? 100 : 30)
    );

    getDocs(moreMessagesQuery).then(snapshot => {
      if (snapshot.empty) {
        setHasMoreMessages(false);
        setLoadingMore(false);
        return;
      }

      lastDocumentRef.current = snapshot.docs[snapshot.docs.length - 1];

      let additionalMessages = snapshot.docs.map(doc =>
        mapModerationDocToMessage(doc.id, doc.data() as Record<string, unknown>)
      );
      
      if (showFlaggedOnly) {
        additionalMessages = additionalMessages.filter(msg => msg.isFlaggedMessage);
      }
      
      setMessages(prev => [...prev, ...additionalMessages]);
      setHasMoreMessages(additionalMessages.length === (showFlaggedOnly ? 100 : 30));
      setLoadingMore(false);
    }).catch(err => {
      console.error("Error loading more messages:", err);
      setLoadingMore(false);
    });
  };

  const loadImageUrls = async () => {
    const messagesToLoad = messages.filter(
      message => (message.type === 'image' || message.type == 'stickerMessage') && 
      !imageUrls[message.id] && 
      !loadingImages[message.id]
    );
    
    if (messagesToLoad.length === 0) return;
    
    const newLoadingStates: Record<string, boolean> = {};
    messagesToLoad.forEach(message => {
      newLoadingStates[message.id] = true;
    });
    
    if (Object.keys(newLoadingStates).length > 0) {
      setLoadingImages(prev => ({
        ...prev,
        ...newLoadingStates
      }));
    }
    
    const newImageUrls: Record<string, string> = {};
    
    await Promise.all(messagesToLoad.map(async (message) => {
      try {
        // Primary: load from Firebase Storage via content.storagePath
        if (message.content?.storagePath) {
          const imageRef = ref(storage, message.content.storagePath);
          try {
            const url = await getDownloadURL(imageRef);
            if (url) {
              newImageUrls[message.id] = url;
              return;
            }
          } catch (storageErr) {
            console.error(`Failed to load image from content.storagePath for message ${message.id}:`, storageErr);
          }
        }
        // Fallback: use jpegThumbnail from WhatsApp message data (base64)
        const imgMsg = message.message?.imageMessage;
        if (imgMsg) {
          const thumb = (imgMsg as Record<string, unknown>)?.jpegThumbnail;
          if (typeof thumb === 'string' && thumb.length > 0) {
            newImageUrls[message.id] = `data:image/jpeg;base64,${thumb}`;
          }
        }
      } catch (err) {
        console.error(`Failed to load image for message ${message.id}:`, err);
      }
    }));
    
    if (Object.keys(newLoadingStates).length > 0) {
      setLoadingImages(prev => {
        const updated = {...prev};
        Object.keys(newLoadingStates).forEach(id => {
          updated[id] = false;
        });
        return updated;
      });
    }
    
    if (Object.keys(newImageUrls).length > 0) {
      setImageUrls(prev => ({
        ...prev,
        ...newImageUrls
      }));
    }
  };

  const loadAudioUrls = async () => {
    const messagesToLoad = messages.filter(
      message => message.type === 'audioMessage' && 
      !audioUrls[message.id] && 
      !loadingAudios[message.id] &&
      message.content?.storagePath
    );
    
    if (messagesToLoad.length === 0) return;
    
    const newLoadingStates: Record<string, boolean> = {};
    messagesToLoad.forEach(message => {
      newLoadingStates[message.id] = true;
    });
    
    if (Object.keys(newLoadingStates).length > 0) {
      setLoadingAudios(prev => ({
        ...prev,
        ...newLoadingStates
      }));
    }
    
    const newAudioUrls: Record<string, string> = {};
    
    await Promise.all(messagesToLoad.map(async (message) => {
      try {
        if (message.content?.storagePath) {
          const audioRef = ref(storage, message.content.storagePath);
          try {
            const url = await getDownloadURL(audioRef);
            if (url) {
              newAudioUrls[message.id] = url;
            }
          } catch (storageErr) {
            console.error(`Failed to load audio from content.storagePath for message ${message.id}:`, storageErr);
          }
        }
      } catch (err) {
        console.error(`Failed to load audio for message ${message.id}:`, err);
      }
    }));
    
    if (Object.keys(newLoadingStates).length > 0) {
      setLoadingAudios(prev => {
        const updated = {...prev};
        Object.keys(newLoadingStates).forEach(id => {
          updated[id] = false;
        });
        return updated;
      });
    }
    
    if (Object.keys(newAudioUrls).length > 0) {
      setAudioUrls(prev => ({
        ...prev,
        ...newAudioUrls
      }));
    }
  };

  const submitFeedback = async (messageId: string, feedback: 'thumbs-up' | 'thumbs-down') => {
    if (!auth.currentUser) {
      toast({
        title: "Not signed in",
        description: "You need to be signed in to submit feedback",
        variant: "destructive"
      });
      return;
    }

    if (messageFeedback[messageId]) {
      toast({
        title: "Feedback already submitted",
        description: "You've already provided feedback for this message",
      });
      return;
    }

    setSubmittingFeedback(prev => ({
      ...prev,
      [messageId]: true
    }));

    try {
      const messageRef = doc(db, `connections/${connectionId}/conversations/${conversation.id}/messages/${messageId}`);
      
      await addDoc(
        collection(db, `feedback/${auth.currentUser.uid}/items`), 
        {
          messageRef: messageRef,
          feedback: feedback,
          createdAt: new Date(),
          connectionId,
          conversationId: conversation.id
        }
      );

      setMessageFeedback(prev => ({
        ...prev,
        [messageId]: feedback
      }));
      
      toast({
        title: "Feedback submitted",
        description: "Thank you for your feedback",
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmittingFeedback(prev => ({
        ...prev,
        [messageId]: false
      }));
    }
  };

  const toggleMessageVisibility = (messageId: string) => {
    setHiddenMessages(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const toggleMessageHistory = (messageId: string) => {
    setExpandedHistories(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const toggleExplanation = (messageId: string) => {
    setExpandedExplanations(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const openImagePreview = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'image':
        return <Image className="h-6 w-6 text-muted-foreground" />;
      case 'stickerMessage':
        return <Sticker className="h-6 w-6 text-muted-foreground" />;
      case 'extendedText':
      case 'text':
        return <FileText className="h-6 w-6 text-muted-foreground" />;
      case 'video':
        return <Video className="h-6 w-6 text-muted-foreground" />;
      case 'audio':
      case 'audioMessage':
        return <Mic className="h-6 w-6 text-muted-foreground" />;
      default:
        return <File className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getMessageTypeName = (type: string): string => {
    switch (type) {
      case 'stickerMessage':
        return 'sticker';
      case 'image':
        return 'image';
      case 'extendedText':
      case 'text':
        return 'text';
      case 'video':
        return 'video';
      case 'audio':
      case 'audioMessage':
        return 'audio';
      default:
        return 'content';
    }
  };

  const renderMessageContent = (message: ConversationMessageData, isEdit: boolean = false) => {
    if (!message.message) {
      return <div className="text-sm text-muted-foreground italic">
          Empty message content
        </div>;
    }
    const isFlagged = message.isFlaggedMessage;
    const isHidden = hiddenMessages[message.id] && isFlagged;
    
    // Render the main message content
    const renderMainContent = () => {
      if (message.type === 'extendedText' || message.type === 'text' && message.message.extendedTextMessage) {
        return <LinkifiedText text={message.message.extendedTextMessage?.text || ''} />; 
      }
      if (message.type === 'text' && message.message.conversation) {
        return <LinkifiedText text={message.message.conversation || ''} />;
      }
      if (message.type === 'image' && message.message.imageMessage) {
        return <div className="space-y-2">
          {renderImageContent(message, isEdit)}
          {message.message.imageMessage.caption && 
            <LinkifiedText text={message.message.imageMessage.caption || ''} /> 
          }
        </div>;
      }
      if (message.type === 'stickerMessage') {
        return <div className="space-y-2">
          {renderImageContent(message, isEdit)}
        </div>;
      }
      if (message.type === 'audioMessage') {
        return <div className="space-y-2">
          {renderAudioContent(message, isEdit)}
        </div>;
      }
      return <div className="text-sm text-muted-foreground italic">
        Unsupported message type: {message.type}
      </div>;
    };
    
    // Render message edits if they exist
    const renderEdits = () => {
      if (!message.edits || message.edits.length === 0) {
        return null;
      }
      
      return (
        <div className={`mt-3 space-y-2 ${isEdit ? 'ml-4' : 'ml-6'} border-l-2 border-muted pl-3`}>
          <div className="text-xs text-muted-foreground font-medium mb-2">
            Message Edits ({message.edits.length})
          </div>
          {message.edits.map((edit, index) => (
            <div key={`${message.id}-edit-${index}`} className="space-y-1">
              <div className="text-xs text-muted-foreground">
                Edit #{index + 1} {edit.isFlaggedMessage && <Badge variant="destructive" className="text-[10px] h-3 ml-1">Flagged</Badge>}
              </div>
              <div className={`${isEdit ? 'text-sm' : ''}`}>
                {renderMessageContent(edit, true)}
              </div>
            </div>
          ))}
        </div>
      );
    };
    
    if (isFlagged) {
      return <Card className="border-destructive/50 bg-background shadow-sm w-full">
          <CardHeader className="p-3 pb-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-destructive" />
                <CardTitle className="text-sm font-medium text-destructive">
                  {isEdit ? 'Flagged Edit' : 'Flagged Content'}
                </CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={() => toggleMessageVisibility(message.id)} className="h-8 px-2 text-xs">
                {isHidden ? <>
                    <Eye className="h-3 w-3 mr-1" />
                    <span>Show</span>
                  </> : <>
                    <EyeOff className="h-3 w-3 mr-1" />
                    <span>Hide</span>
                  </>}
              </Button>
            </div>
            <CardDescription className="text-xs text-muted-foreground mt-1">
              {message.evaluation?.decision?.userFriendlyExplanation ? (
                <div className="relative">
                  <div 
                    className={`${
                      expandedExplanations[message.id] 
                        ? '' 
                        : 'max-h-[25px] overflow-hidden'
                    } transition-all duration-200 cursor-pointer`}
                    onClick={() => toggleExplanation(message.id)}
                  >
                    <p className="whitespace-pre-wrap">
                      {message.evaluation.decision.userFriendlyExplanation}
                    </p>
                    {!expandedExplanations[message.id] && message.evaluation.decision.userFriendlyExplanation.length > 50 && (
                      <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent pointer-events-none" style={{ top: '0', bottom: '0', opacity: 0.9 }}></div>
                    )}
                  </div>
                </div>
              ) : (
                `This ${isEdit ? 'edit' : 'message'} was flagged for potentially sensitive content`
              )}
            </CardDescription>
          </CardHeader>
          
          {message.moderation && <CardFooter className="p-3 pb-0">
              <div className="flex flex-wrap gap-1.5 w-full">
                {(() => {
              const textScores = message.moderation?.text?.categoryScores;
              const imageScores = message.moderation?.image?.categoryScores;
              const scores = textScores || imageScores;
              if (!scores) return null;
              return Object.entries(scores).filter(([_, score]) => {
                return typeof score === 'number' && score > 0.2;
              }).sort(([_, scoreA], [__, scoreB]) => {
                if (typeof scoreA === 'number' && typeof scoreB === 'number') {
                  return scoreB - scoreA;
                }
                return 0;
              }).map(([category, score]) => {
                if (typeof score === 'number') {
                  return <Badge key={category} variant="outline" className={`${getScoreColorClass(score)} capitalize`}>
                            {category.toLowerCase().replace(/_/g, ' ')}: {Math.round(score * 100)}%
                          </Badge>;
                }
                return null;
              });
            })()}
              </div>
            </CardFooter>}
          
          <CardContent className="p-3">
            {isHidden ? <div className="rounded-md bg-muted p-4 text-center cursor-pointer hover:bg-muted/80 transition-colors" onClick={() => toggleMessageVisibility(message.id)}>
                <p className="text-sm text-muted-foreground">Click to view {getMessageTypeName(message.type)} content</p>
              </div> : <div className="rounded-md bg-muted/50 p-3 text-foreground">
                {renderMainContent()}
                {!isEdit && renderEdits()}
              </div>}
          </CardContent>
          
          {!isEdit && <CardFooter className="p-3 pt-0 flex justify-end">
            <div className="flex gap-2 mt-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(message.id, 'thumbs-up')}
                disabled={!!messageFeedback[message.id] || submittingFeedback[message.id]}
                className={`${messageFeedback[message.id] === 'thumbs-up' ? 'bg-green-100 dark:bg-green-900/30 text-green-900 dark:text-green-300 border-green-300' : ''}`}
              >
                <ThumbsUp className="h-4 w-4 mr-1" />
                <span>Helpful</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => submitFeedback(message.id, 'thumbs-down')}
                disabled={!!messageFeedback[message.id] || submittingFeedback[message.id]}
                className={`${messageFeedback[message.id] === 'thumbs-down' ? 'bg-red-100 dark:bg-red-900/30 text-red-900 dark:text-red-300 border-red-300' : ''}`}
              >
                <ThumbsDown className="h-4 w-4 mr-1" />
                <span>Not Helpful</span>
              </Button>
            </div>
          </CardFooter>}
        </Card>;
    }
    
    // For non-flagged messages, render content with edits
    return (
      <div className="space-y-2">
        {renderMainContent()}
        {!isEdit && renderEdits()}
      </div>
    );
  };

  const renderImageContent = (message: ConversationMessageData, isEdit: boolean) => {
    if (message.type !== 'image' && message.type !== 'stickerMessage') return null;
    const imageUrl = imageUrls[message.id];
    const isLoading = loadingImages[message.id];
    const caption = message.message?.imageMessage?.caption;
    return <div className="space-y-2">
      
      {isEdit ? <></> : isLoading ? <Skeleton className="h-32 w-full rounded-md" /> : imageUrl ? <img src={imageUrl} alt={caption || "Media content"} className="rounded-md max-h-48 object-cover cursor-pointer" onClick={() => openImagePreview(imageUrl)} /> : <div className="rounded-md bg-muted p-4 text-center">
            <ImageOff className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Image not available</p>
          </div>}
        
        {caption && <div className="text-sm text-foreground">{caption}</div>}
      </div>;
  };

  const renderAudioContent = (message: ConversationMessageData, isEdit: boolean) => {
    if (message.type !== 'audioMessage') return null;
    const audioUrl = audioUrls[message.id];
    const isLoading = loadingAudios[message.id];
    const audioMessage = message.message?.audioMessage;
    const audioData = message.content?.voiceData;

    return <div className="space-y-2">

      {isEdit ? <></> : isLoading ?
        <Skeleton className="h-16 w-full rounded-md" /> :
        audioUrl ?
          <AudioPlayer
            audioUrl={audioUrl}
            duration={audioData?.duration || 0}
            transcription={audioData?.transcription || ''}
          /> :
          <div className="rounded-md bg-muted p-4 text-center">
            <Mic className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Audio not available</p>
          </div>
      }
    </div>;
  };

  // ADC-006A F12: getScoreColorClass extracted to @/lib/incidentUtils.ts

  const convertLinksToAnchors = (text: string) => {
    // Regular expression to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Replace URLs with <a> elements
    const textWithLinks = text.replace(urlRegex, (url) => {
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`;
    });
    
    return textWithLinks;
  };

  if (loading && !loadingMore) {
    return <div className={`space-y-4 ${className}`}>
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
      </div>;
  }

  if (error) {
    return <div className={`p-4 text-destructive ${className}`}>
        {error}
      </div>;
  }

  if (messages.length === 0) {
    return <div className={`p-4 space-y-4 ${className}`}>
        <div className="text-center text-muted-foreground p-8">
          <div className="flex flex-col items-center">
            <MessageSquareDashed className="h-12 w-12 text-muted-foreground mb-2" />
            <p>No messages in this conversation</p>
          </div>
        </div>
      </div>;
  }

  const hasNewMessages = initialLastViewTimestampRef.current !== null &&
    messages.some(msg => msg.messageTimestamp > initialLastViewTimestampRef.current!);
  
  const newMessages = initialLastViewTimestampRef.current !== null ? 
    messages.filter(msg => msg.messageTimestamp > initialLastViewTimestampRef.current!) : [];
  
  const oldMessages = initialLastViewTimestampRef.current !== null ? 
    messages.filter(msg => msg.messageTimestamp <= initialLastViewTimestampRef.current!) : messages;

  const hasOldMessages = oldMessages.length > 0;

  return <div className={`space-y-4 ${className}`}>
      <div className="space-y-4">
        {hasNewMessages && newMessages.map(message => {
          const timestamp = new Date(message.messageTimestamp * 1000);
          const formattedDate = format(timestamp, 'MMM d, yyyy h:mm a');
          const fromMe = message.fromMe;
          const senderName = fromMe 
            ? "You" 
            : message.sender?.contact?.name || 
              message.sender?.contact?.notify || 
              message.pushName || 
              "Unknown";
          const isFlagged = message.isFlaggedMessage;
          const isDeleted = message.isDeleted;
          const isUnread = initialLastViewTimestampRef.current !== null && 
                          message.messageTimestamp > initialLastViewTimestampRef.current;
          
          return <div key={message.id} className={`flex flex-col ${fromMe ? 'items-end' : 'items-start'}`}>
              <div className="flex items-center mb-1 text-xs text-muted-foreground">
                {getMessageTypeIcon(message.type)}
                <span className="ml-1">{senderName}</span>
                <span className="mx-1">•</span>
                <span>{formattedDate}</span>
                {isFlagged && <>
                    <span className="mx-1">•</span>
                    <Badge variant="destructive" className="text-[10px] h-4">Flagged</Badge>
                  </>}
                {isDeleted && <>
                    <span className="mx-1">•</span>
                    <Badge variant="outline" className="text-[10px] h-4 border-red-500 text-red-500">Deleted</Badge>
                  </>}
                {isUnread && <>
                    <span className="mx-1">•</span>
                    <Badge variant="secondary" className="text-[10px] h-4">New</Badge>
                  </>}
              </div>
              
              <div className={`max-w-[80%] ${isFlagged ? 'w-full' : `px-4 py-2 rounded-lg ${isDeleted ? 'opacity-60 line-through' : ''} ${fromMe ? 'bg-primary text-primary-foreground rounded-tr-none' : 'bg-muted text-foreground rounded-tl-none'}`}`}>
                {renderMessageContent(message)}
              </div>
              
              {!isFlagged && message.moderation && <div className="flex flex-wrap gap-1 mt-1 text-xs">
                  {(() => {
                    const textScores = message.moderation?.text?.categoryScores;
                    const imageScores = message.moderation?.image?.categoryScores;
                    const scores = textScores || imageScores;
                    const hasExplanation = message.evaluation?.decision?.userFriendlyExplanation;
                    
                    if (!scores) return null;
                    
                    const scoresToShow = Object.entries(scores)
                      .filter(([_, score]) => {
                        return typeof score === 'number' && score > 0.2;
                      })
                      .sort(([_, scoreA], [__, scoreB]) => {
                        if (typeof scoreA === 'number' && typeof scoreB === 'number') {
                          return scoreB - scoreA;
                        }
                        return 0;
                      })
                      .slice(0, 3);
                      
                    return (
                      <div className="flex items-center gap-1">
                        {scoresToShow.map(([category, score]) => {
                          if (typeof score === 'number') {
                            return <Badge key={category} variant="outline" className={getScoreColorClass(score)}>
                                    {category}: {Math.round(score * 100)}%
                                  </Badge>;
                          }
                          return null;
                        })}
                        
                        {hasExplanation && (
                          isMobile ? (
                            <Popover>
                              <PopoverTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors ml-1" />
                              </PopoverTrigger>
                              <PopoverContent className="max-w-xs text-xs p-3">
                                {message.evaluation?.decision?.userFriendlyExplanation}
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-pointer ml-1">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs whitespace-pre-wrap text-xs">
                                  {message.evaluation?.decision?.userFriendlyExplanation}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )
                        )}
                      </div>
                    );
                  })()}
                </div>}
            </div>;
        })}
        
        {hasNewMessages && hasOldMessages && (
          <div className="py-2">
            <div className="flex items-center gap-2">
              <Separator className="flex-1" />
              <span className="text-xs text-muted-foreground font-medium">Previous Messages</span>
              <Separator className="flex-1" />
            </div>
          </div>
        )}
        
        {oldMessages.map(message => {
          const timestamp = new Date(message.messageTimestamp * 1000);
          const formattedDate = format(timestamp, 'MMM d, yyyy h:mm a');
          const fromMe = message.fromMe;
          const senderName = fromMe 
            ? "You" 
            : message.sender?.contact?.name || 
              message.sender?.contact?.notify || 
              message.pushName || 
              "Unknown";
          const isFlagged = message.isFlaggedMessage;
          const isDeleted = message.isDeleted;
          
          return <div key={message.id} className={`flex flex-col ${fromMe ? 'items-end' : 'items-start'}`}>
              <div className="flex items-center mb-1 text-xs text-muted-foreground">
                {getMessageTypeIcon(message.type)}
                <span className="ml-1">{senderName}</span>
                <span className="mx-1">•</span>
                <span>{formattedDate}</span>
                {isFlagged && <>
                    <span className="mx-1">•</span>
                    <Badge variant="destructive" className="text-[10px] h-4">Flagged</Badge>
                  </>}
                {isDeleted && <>
                    <span className="mx-1">•</span>
                    <Badge variant="outline" className="text-[10px] h-4 border-red-500 text-red-500">Deleted</Badge>
                  </>}
              </div>
              
              <div className={`max-w-[80%] ${isFlagged ? 'w-full' : `px-4 py-2 rounded-lg ${isDeleted ? 'opacity-60 line-through' : ''} ${fromMe ? 'bg-primary text-primary-foreground rounded-tr-none' : 'bg-muted text-foreground rounded-tl-none'}`}`}>
                {renderMessageContent(message)}
              </div>
              
              {!isFlagged && message.moderation && <div className="flex flex-wrap gap-1 mt-1 text-xs">
                  {(() => {
                    const textScores = message.moderation?.text?.categoryScores;
                    const imageScores = message.moderation?.image?.categoryScores;
                    const scores = textScores || imageScores;
                    const hasExplanation = message.evaluation?.decision?.userFriendlyExplanation;
                    
                    if (!scores) return null;
                    
                    const scoresToShow = Object.entries(scores)
                      .filter(([_, score]) => {
                        return typeof score === 'number' && score > 0.2;
                      })
                      .sort(([_, scoreA], [__, scoreB]) => {
                        if (typeof scoreA === 'number' && typeof scoreB === 'number') {
                          return scoreB - scoreA;
                        }
                        return 0;
                      })
                      .slice(0, 3);
                      
                    return (
                      <div className="flex items-center gap-1">
                        {scoresToShow.map(([category, score]) => {
                          if (typeof score === 'number') {
                            return <Badge key={category} variant="outline" className={getScoreColorClass(score)}>
                                    {category}: {Math.round(score * 100)}%
                                  </Badge>;
                          }
                          return null;
                        })}
                        
                        {hasExplanation && (
                          isMobile ? (
                            <Popover>
                              <PopoverTrigger>
                                <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors ml-1" />
                              </PopoverTrigger>
                              <PopoverContent className="max-w-xs text-xs p-3">
                                {message.evaluation?.decision?.userFriendlyExplanation}
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-pointer ml-1">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors" />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs whitespace-pre-wrap text-xs">
                                  {message.evaluation?.decision?.userFriendlyExplanation}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )
                        )}
                      </div>
                    );
                  })()}
                </div>}
            </div>;
        })}
      </div>
      
      {hasMoreMessages && <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={loadMoreMessages} disabled={loadingMore} className="gap-2">
            {loadingMore ? <>Loading...</> : <>
                <ChevronDown className="h-4 w-4" />
                <span>Load More</span>
              </>}
          </Button>
        </div>}
      
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogTitle>Image Preview</DialogTitle>
          {selectedImage && <div className="flex items-center justify-center w-full overflow-hidden">
              <img src={selectedImage} alt="Full size preview" className="max-h-[70vh] object-contain" />
            </div>}
        </DialogContent>
      </Dialog>
      
      <div ref={messagesEndRef} />
    </div>;
}

